import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  MessageCircle,
  X,
  Send,
  Users,
  Clock,
  ChevronRight,
  User,
  Building2,
  Trash2,
  Bell,
  BellOff,
  MoreVertical,
  Phone,
  Video,
  Archive,
  Pin,
  Paperclip,
  Smile,
  MoreHorizontal,
  Settings,
  Moon,
  Sun,
  Check,
  CheckCheck,
  Edit2,
  Image,
  File,
  FileText,
  Download,
  Loader2,
} from 'lucide-react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  getDocs,
  limit,
  deleteDoc,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../../database/firebase';
import { storage } from '../../database/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';

const MessageList = ({
  chatId,
  currentUserId,
  isDarkMode = false,
  onEditMessage,
  onDeleteMessage,
  editingMessageId,
  setEditingMessageId,
  editText,
  setEditText,
  openMessageOptions,
  setOpenMessageOptions,
  canEditDelete = true,
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!chatId) return;

    const messagesQuery = query(
      collection(firestore, 'private_chats', chatId, 'messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, snapshot => {
      const messageData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      }));

      const uniqueMessages = messageData.filter(
        (message, index, self) => index === self.findIndex(m => m.id === message.id)
      );

      setMessages(uniqueMessages);
      setLoading(false);
      setTimeout(scrollToBottom, 100);
    });

    return () => unsubscribe();
  }, [chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatTime = date => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div
      className={`flex-1 overflow-y-auto px-4 py-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
    >
      <div className="space-y-4">
        {messages.map((message, index) => {
          const isOwnMessage = message.senderId === currentUserId;
          const showAvatar = index === 0 || messages[index - 1]?.senderId !== message.senderId;
          const showTimestamp =
            index === 0 ||
            new Date(message.createdAt) - new Date(messages[index - 1]?.createdAt) > 5 * 60 * 1000;

          return (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`}
            >
              <div
                className={`flex max-w-[75%] ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} items-end gap-2`}
              >
                {!isOwnMessage && (
                  <div className="flex-shrink-0">
                    {showAvatar ? (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-sm font-medium text-white">
                        {message.senderName?.charAt(0) || 'U'}
                      </div>
                    ) : (
                      <div className="h-8 w-8" />
                    )}
                  </div>
                )}

                <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                  {!isOwnMessage && showTimestamp && (
                    <div className="mb-1 flex items-center gap-2">
                      <span
                        className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
                      >
                        {message.senderName}
                      </span>
                      <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formatTime(message.createdAt)}
                      </span>
                    </div>
                  )}

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className={`relative rounded-2xl px-4 py-2 shadow-sm ${
                      isOwnMessage
                        ? 'rounded-br-md bg-blue-600 text-white'
                        : isDarkMode
                          ? 'rounded-bl-md border border-gray-700 bg-gray-800 text-white'
                          : 'rounded-bl-md border border-gray-200 bg-white text-gray-900'
                    }`}
                  >
                    {editingMessageId === message.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          className={`flex-1 border-none bg-transparent text-sm outline-none ${
                            isOwnMessage
                              ? 'text-white'
                              : isDarkMode
                                ? 'text-white'
                                : 'text-gray-900'
                          }`}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              onEditMessage(message.id);
                            }
                            if (e.key === 'Escape') {
                              setEditingMessageId(null);
                              setEditText('');
                            }
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => onEditMessage(message.id)}
                          className="rounded p-1 hover:bg-white/20"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingMessageId(null);
                            setEditText('');
                          }}
                          className="rounded p-1 hover:bg-white/20"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <>
                        {message.text && (
                          <p className="whitespace-pre-wrap text-sm leading-relaxed">
                            {message.text}
                          </p>
                        )}
                        {message.files && message.files.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {message.files.map((file, idx) => {
                              const isImage = file.type?.startsWith('image/');
                              const isVideo = file.type?.startsWith('video/');
                              const fileSize = file.size
                                ? (file.size / 1024 / 1024).toFixed(2) + ' MB'
                                : '';

                              return (
                                <div key={idx} className="overflow-hidden rounded-lg">
                                  {isImage ? (
                                    <a
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block"
                                    >
                                      <img
                                        src={file.url}
                                        alt={file.name}
                                        className="max-h-96 max-w-full cursor-pointer rounded-lg transition-opacity hover:opacity-90"
                                      />
                                    </a>
                                  ) : isVideo ? (
                                    <video
                                      src={file.url}
                                      controls
                                      className="max-h-96 max-w-full rounded-lg"
                                    >
                                      Your browser does not support the video tag.
                                    </video>
                                  ) : (
                                    <a
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-3 rounded-lg p-3 transition-colors ${
                                        isOwnMessage
                                          ? 'bg-white/20 hover:bg-white/30'
                                          : isDarkMode
                                            ? 'bg-gray-700 hover:bg-gray-600'
                                            : 'bg-gray-100 hover:bg-gray-200'
                                      }`}
                                    >
                                      <FileText
                                        size={24}
                                        className={
                                          isOwnMessage
                                            ? 'text-white'
                                            : isDarkMode
                                              ? 'text-gray-300'
                                              : 'text-gray-600'
                                        }
                                      />
                                      <div className="min-w-0 flex-1">
                                        <p
                                          className={`truncate text-sm font-medium ${
                                            isOwnMessage
                                              ? 'text-white'
                                              : isDarkMode
                                                ? 'text-white'
                                                : 'text-gray-900'
                                          }`}
                                        >
                                          {file.name}
                                        </p>
                                        {fileSize && (
                                          <p
                                            className={`text-xs ${
                                              isOwnMessage
                                                ? 'text-white/70'
                                                : isDarkMode
                                                  ? 'text-gray-400'
                                                  : 'text-gray-500'
                                            }`}
                                          >
                                            {fileSize}
                                          </p>
                                        )}
                                      </div>
                                      <Download
                                        size={18}
                                        className={
                                          isOwnMessage
                                            ? 'text-white'
                                            : isDarkMode
                                              ? 'text-gray-300'
                                              : 'text-gray-600'
                                        }
                                      />
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}

                    {isOwnMessage && editingMessageId !== message.id && (
                      <div className="mt-1 flex items-center justify-end gap-2">
                        <span className="text-xs text-blue-200">
                          {formatTime(message.createdAt)}
                        </span>
                        <div className="flex items-center">
                          <CheckCheck size={12} className="text-blue-200" />
                        </div>
                      </div>
                    )}
                  </motion.div>

                  {isOwnMessage && canEditDelete && editingMessageId !== message.id && (
                    <div
                      className={`relative mt-1 flex items-center gap-1 ${isOwnMessage ? 'justify-end' : 'justify-start'} opacity-0 transition-opacity group-hover:opacity-100`}
                    >
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setOpenMessageOptions(
                            openMessageOptions === message.id ? null : message.id
                          );
                        }}
                        className={`rounded-lg p-1 text-xs transition-colors ${
                          isDarkMode
                            ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        }`}
                      >
                        <MoreVertical size={14} />
                      </button>
                      {openMessageOptions === message.id && (
                        <div
                          data-menu
                          className={`absolute bottom-full right-0 z-10 mb-2 rounded-xl border shadow-xl ${
                            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                          }`}
                          onClick={e => e.stopPropagation()}
                        >
                          <div className="py-2">
                            <button
                              onClick={() => {
                                setEditingMessageId(message.id);
                                setEditText(message.text || '');
                                setOpenMessageOptions(null);
                              }}
                              className={`flex w-full items-center gap-2 px-4 py-2 text-xs transition-colors ${
                                isDarkMode
                                  ? 'text-gray-300 hover:bg-gray-700'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>
                            <button
                              onClick={() => onDeleteMessage(message.id)}
                              className={`flex w-full items-center gap-2 px-4 py-2 text-xs transition-colors ${
                                isDarkMode
                                  ? 'text-red-400 hover:bg-red-900/20'
                                  : 'text-red-600 hover:bg-red-50'
                              }`}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

const ChatItem = ({
  chat,
  isSelected,
  currentUserData,
  onSelect,
  onDelete,
  onMute,
  onArchive,
  isDarkMode = false,
}) => {
  const [showActions, setShowActions] = useState(false);

  const formatTime = date => {
    if (!date) return '';

    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return date.toLocaleDateString();
  };

  const getInitials = (firstname, lastname) => {
    const first = firstname ? firstname[0] : '';
    const last = lastname ? lastname[0] : '';
    return (first + last).toUpperCase() || '?';
  };

  const getRoleIcon = role => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return <User className="h-3 w-3 text-red-500" />;
      case 'moderator':
        return <Users className="h-3 w-3 text-blue-500" />;
      case 'client':
        return <Building2 className="h-3 w-3 text-purple-500" />;
      case 'alumni':
      default:
        return <User className="h-3 w-3 text-green-500" />;
    }
  };

  return (
    <motion.div
      className={`group relative cursor-pointer transition-all duration-200 ${
        isSelected
          ? isDarkMode
            ? 'border-l-4 border-l-blue-500 bg-gray-800'
            : 'border-l-4 border-l-blue-500 bg-blue-50'
          : isDarkMode
            ? 'hover:bg-gray-800'
            : 'hover:bg-gray-50'
      } ${chat.unread ? 'font-semibold' : ''}`}
      onClick={() => onSelect(chat)}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="flex items-center gap-3 p-4">
        <div className="relative flex-shrink-0">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-sm font-semibold text-white shadow-lg ${
              chat.unread ? 'ring-2 ring-blue-400' : ''
            }`}
          >
            {chat.otherParticipant
              ? getInitials(chat.otherParticipant.firstname, chat.otherParticipant.lastname)
              : '?'}
          </div>
          <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white bg-green-500"></div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className={`truncate font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
              >
                {chat.otherParticipant
                  ? `${chat.otherParticipant.firstname || ''} ${chat.otherParticipant.lastname || ''}`.trim() ||
                    'Unknown User'
                  : 'Unknown User'}
              </span>
              {getRoleIcon(chat.otherParticipant?.role)}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatTime(chat.lastMessageAt)}
              </span>
              {chat.unread && <div className="h-2 w-2 rounded-full bg-blue-500"></div>}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className={`truncate text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {chat.lastMessage ? (
                <>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    {chat.lastSenderId === currentUserData?.id ? 'You: ' : ''}
                  </span>
                  {chat.lastMessage}
                </>
              ) : (
                <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                  No messages yet
                </span>
              )}
            </p>
            {isSelected && <ChevronRight className="h-4 w-4 text-blue-500" />}
          </div>
        </div>
      </div>
      <AnimatePresence>
        {showActions && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-1"
          >
            <button
              onClick={e => {
                e.stopPropagation();
                onMute(chat.id);
              }}
              className="rounded-full bg-gray-200 p-2 text-gray-600 transition-colors hover:bg-gray-300"
            >
              <BellOff size={14} />
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                onArchive(chat.id);
              }}
              className="rounded-full bg-gray-200 p-2 text-gray-600 transition-colors hover:bg-gray-300"
            >
              <Archive size={14} />
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                onDelete(chat.id);
              }}
              className="rounded-full bg-red-100 p-2 text-red-600 transition-colors hover:bg-red-200"
            >
              <Trash2 size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Mailbox = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [openMessageOptions, setOpenMessageOptions] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const selectedChatRef = useRef(null);
  const chatsRef = useRef([]);
  const isInitialMount = useRef(true);
  const previousChatsRef = useRef([]);

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousChatsRef.current = chats;
      return;
    }

    if (!selectedChatRef.current || chats.length === 0) {
      previousChatsRef.current = chats;
      return;
    }

    const currentChatId = selectedChatRef.current.id;
    const updatedChat = chats.find(chat => chat.id === currentChatId);

    if (!updatedChat) {
      previousChatsRef.current = chats;
      return;
    }

    // Only update if specific fields changed (not the entire object)
    const currentChat = selectedChatRef.current;
    const hasChanged =
      updatedChat.lastMessage !== currentChat.lastMessage ||
      updatedChat.lastMessageAt?.getTime() !== currentChat.lastMessageAt?.getTime() ||
      updatedChat.unread !== currentChat.unread ||
      updatedChat.lastSenderId !== currentChat.lastSenderId;

    if (hasChanged) {
      setSelectedChat(prev => ({
        ...prev,
        ...updatedChat,
      }));
    }

    previousChatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const fetchCurrentUserData = async () => {
      try {
        const collections = ['alumni', 'clients', 'moderator', 'admin'];

        for (const collectionName of collections) {
          const q = query(
            collection(firestore, collectionName),
            where('email', '==', user.email),
            limit(1)
          );

          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const userData = {
              id: snapshot.docs[0].id,
              ...snapshot.docs[0].data(),
              role: collectionName,
            };
            setCurrentUserData(userData);
            break;
          }
        }
      } catch (error) {
        toast.error('Error fetching current user data:', error);
        throw error;
      }
    };

    fetchCurrentUserData();
  }, [user]);

  useEffect(() => {
    if (!user || !currentUserData || !currentUserData.id) return;

    setChatsLoading(true);
    const chatsQuery = query(
      collection(firestore, 'private_chats'),
      where('participants', 'array-contains', currentUserData.id),
      orderBy('lastMessageAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      chatsQuery,
      snapshot => {
        const chatData = snapshot.docs.map(doc => {
          const data = doc.data();
          const otherParticipant = data.participantDetails?.find(
            p => p.userId !== currentUserData.id
          );

          return {
            id: doc.id,
            ...data,
            otherParticipant,
            unread: data.unreadFor?.includes(currentUserData.id) || false,
            lastMessageAt: data.lastMessageAt?.toDate() || new Date(),
          };
        });

        setChats(chatData);
        setChatsLoading(false);
      },
      error => {
        toast.error('Error fetching chats: ' + error.message);
        setChatsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, currentUserData?.id]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!currentUserData) return;

      setUsersLoading(true);
      try {
        const allUsers = [];
        const collections = [
          { name: 'alumni', role: 'Alumni' },
          { name: 'clients', role: 'Client' },
          { name: 'moderator', role: 'Moderator' },
          { name: 'admin', role: 'Admin' },
        ];

        for (const { name: collectionName, role } of collections) {
          const snapshot = await getDocs(collection(firestore, collectionName));
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            if (doc.id !== currentUserData.id) {
              allUsers.push({
                id: doc.id,
                firstname: data.firstname || '',
                lastname: data.lastname || '',
                email: data.email || '',
                phone: data.phone || '',
                role: role,
                program: data.program || '',
                businessStage: data.businessStage || '',
                department: data.department || '',
                company: data.company || '',
              });
            }
          });
        }

        setUsers(allUsers);
      } catch (error) {
        toast.error('Error fetching users:', error);
        throw error;
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [currentUserData]);

  const filteredChats = chats.filter(chat => {
    if (!chatSearchTerm) return true;
    const otherParticipant = chat.otherParticipant;
    if (!otherParticipant) return false;

    const fullName =
      `${otherParticipant.firstname || ''} ${otherParticipant.lastname || ''}`.toLowerCase();
    const lastMessage = chat.lastMessage?.toLowerCase() || '';
    const searchLower = chatSearchTerm.toLowerCase();

    return fullName.includes(searchLower) || lastMessage.includes(searchLower);
  });

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return [];
    const searchLower = searchTerm.toLowerCase();
    return users.filter(user => {
      const firstnameMatch = user.firstname?.toLowerCase().includes(searchLower);
      const lastnameMatch = user.lastname?.toLowerCase().includes(searchLower);
      const emailMatch = user.email?.toLowerCase().includes(searchLower);
      return firstnameMatch || lastnameMatch || emailMatch;
    });
  }, [searchTerm, users]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const sorted = [...filteredUsers].sort((a, b) => {
      const nameA = `${a.firstname || ''} ${a.lastname || ''}`.toLowerCase();
      const nameB = `${b.firstname || ''} ${b.lastname || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setSearchResults(sorted);
  }, [searchTerm, filteredUsers]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (openMessageOptions && !e.target.closest('[data-menu]')) {
        setOpenMessageOptions(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMessageOptions]);

  const startNewChat = async selectedUser => {
    if (!currentUserData) return;

    try {
      const existingChat = chats.find(chat => chat.participants.includes(selectedUser.id));

      if (existingChat) {
        setSelectedChat(existingChat);
        setShowUserSearch(false);
        setSearchTerm('');
        return;
      }

      const chatRef = collection(firestore, 'private_chats');
      const newChatData = {
        participants: [currentUserData.id, selectedUser.id],
        participantDetails: [
          {
            userId: currentUserData.id,
            firstname: currentUserData.firstname || '',
            lastname: currentUserData.lastname || '',
            role: currentUserData.role || '',
          },
          {
            userId: selectedUser.id,
            firstname: selectedUser.firstname || '',
            lastname: selectedUser.lastname || '',
            role: selectedUser.role || '',
          },
        ],
        lastMessageAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadFor: [selectedUser.id],
        lastMessage: '',
        lastSenderId: '',
        lastSenderName: '',
      };

      const docRef = await addDoc(chatRef, newChatData);

      const newChat = {
        id: docRef.id,
        ...newChatData,
        otherParticipant: {
          userId: selectedUser.id,
          firstname: selectedUser.firstname,
          lastname: selectedUser.lastname,
          role: selectedUser.role,
        },
        unread: false,
        lastMessageAt: new Date(),
      };

      setSelectedChat(newChat);
      setShowUserSearch(false);
      setSearchTerm('');
    } catch (error) {
      toast.error('Error creating chat:', error);
      throw error;
    }
  };

  const handleFileSelect = e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file sizes (10MB max per file)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
    e.target.value = ''; // Reset input
  };

  const removeFile = index => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async e => {
    if (e) e.preventDefault();
    const hasText = newMessage.trim().length > 0;
    const hasFiles = selectedFiles.length > 0;

    if ((!hasText && !hasFiles) || !selectedChat || !currentUserData) return;

    const allowedRoles = [
      'admin',
      'client',
      'alumni',
      'moderator',
      'clients',
      'coaches',
      'entrepreneur',
      'moderators',
      'coaches',
      'entrepreneurs',
    ];
    if (!allowedRoles.includes(currentUserData.role?.toLowerCase())) {
      toast.error('User does not have permission to send messages');
      throw new Error('User does not have permission to send messages');
    }

    try {
      // Upload files if any
      let uploadedFiles = [];
      if (hasFiles) {
        setUploadingFiles(selectedFiles.map((_, i) => i));
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          try {
            const timestamp = Date.now();
            const storagePath = `private_chats/${selectedChat.id}/${timestamp}_${file.name}`;
            const storageRef = ref(storage, storagePath);

            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            uploadedFiles.push({
              name: file.name,
              url: downloadURL,
              type: file.type,
              size: file.size,
            });
          } catch (error) {
            toast.error(`Error uploading ${file.name}: ${error.message}`);
          }
        }
        setUploadingFiles([]);
      }

      // Send message if there's text OR files were uploaded
      if (!hasText && uploadedFiles.length === 0) {
        if (hasFiles) {
          toast.error('Failed to upload files. Please try again.');
        }
        return;
      }

      const messageData = {
        chatId: selectedChat.id,
        senderId: currentUserData.id,
        senderName: `${currentUserData.firstname || ''} ${currentUserData.lastname || ''}`.trim(),
        text: hasText ? newMessage.trim() : '',
        createdAt: serverTimestamp(),
        senderRole: currentUserData.role,
      };

      // Only add files field if there are files (Firestore doesn't allow undefined)
      if (uploadedFiles.length > 0) {
        messageData.files = uploadedFiles;
      }

      await addDoc(
        collection(firestore, 'private_chats', selectedChat.id, 'messages'),
        messageData
      );

      const chatRef = doc(firestore, 'private_chats', selectedChat.id);
      const lastMessageText = hasText
        ? newMessage.trim()
        : uploadedFiles.length > 0
          ? `Sent ${uploadedFiles.length} file(s)`
          : '';
      const updatedChatData = {
        lastMessage: lastMessageText,
        lastMessageAt: serverTimestamp(),
        lastSenderId: currentUserData.id,
        lastSenderName:
          `${currentUserData.firstname || ''} ${currentUserData.lastname || ''}`.trim(),
        unreadFor: selectedChat.participants.filter(id => id !== currentUserData.id),
      };

      await updateDoc(chatRef, updatedChatData);

      setSelectedChat(prev => ({
        ...prev,
        lastMessage: lastMessageText,
        lastMessageAt: new Date(),
        lastSenderId: currentUserData.id,
        lastSenderName:
          `${currentUserData.firstname || ''} ${currentUserData.lastname || ''}`.trim(),
        unread: false,
      }));

      setChats(prevChats => {
        const updatedChats = prevChats.map(chat => {
          if (chat.id === selectedChat.id) {
            return {
              ...chat,
              lastMessage: lastMessageText,
              lastMessageAt: new Date(),
              lastSenderId: currentUserData.id,
              lastSenderName:
                `${currentUserData.firstname || ''} ${currentUserData.lastname || ''}`.trim(),
              unread: false,
            };
          }
          return chat;
        });

        return updatedChats.sort((a, b) => b.lastMessageAt - a.lastMessageAt);
      });

      setNewMessage('');
      setSelectedFiles([]);

      if (hasText && uploadedFiles.length > 0) {
        toast.success('Message and files sent successfully');
      } else if (hasText) {
        toast.success('Message sent successfully');
      } else if (uploadedFiles.length > 0) {
        toast.success('Files sent successfully');
      }
    } catch (error) {
      toast.error('Error sending message: ' + error.message);
      setUploadingFiles([]);
      throw error;
    }
  };

  const handleEditMessage = async messageId => {
    if (!editText.trim() || !selectedChat) return;

    try {
      const messageRef = doc(firestore, 'private_chats', selectedChat.id, 'messages', messageId);
      await updateDoc(messageRef, {
        text: editText.trim(),
        updatedAt: serverTimestamp(),
        isEdited: true,
      });
      setEditingMessageId(null);
      setEditText('');
      toast.success('Message updated successfully');
    } catch (error) {
      toast.error('Error updating message: ' + error.message);
    }
  };

  const handleDeleteMessage = async (messageId, confirmed = false) => {
    if (!confirmed) {
      setShowDeleteConfirm({ type: 'message', messageId, chatId: selectedChat.id });
      setOpenMessageOptions(null);
      return;
    }

    try {
      await deleteDoc(doc(firestore, 'private_chats', selectedChat.id, 'messages', messageId));
      setShowDeleteConfirm(null);
      toast.success('Message deleted successfully');
    } catch (error) {
      toast.error('Error deleting message: ' + error.message);
      setShowDeleteConfirm(null);
    }
  };

  const handleDeleteChat = async (chatId, confirmed = false) => {
    if (!confirmed) {
      setShowDeleteConfirm({ type: 'chat', chatId });
      return;
    }

    try {
      await deleteDoc(doc(firestore, 'private_chats', chatId));
      if (selectedChat?.id === chatId) {
        setSelectedChat(null);
      }
      setShowDeleteConfirm(null);
      toast.success('Conversation deleted successfully');
    } catch (error) {
      toast.error('Error deleting chat: ' + error.message);
      setShowDeleteConfirm(null);
    }
  };

  const handleMuteChat = async chatId => {
    try {
      await updateDoc(doc(firestore, 'private_chats', chatId), {
        muted: true,
        mutedAt: serverTimestamp(),
      });
    } catch (error) {
      toast.error('Error muting chat:', error);
      throw error;
    }
  };

  const handleArchiveChat = async chatId => {
    try {
      await updateDoc(doc(firestore, 'private_chats', chatId), {
        archived: true,
        archivedAt: serverTimestamp(),
      });
    } catch (error) {
      toast.error('Error archiving chat:', error);
      throw error;
    }
  };

  const formatTime = date => {
    if (!date) return '';

    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  const getUserIcon = role => {
    switch (role?.toLowerCase()) {
      case 'client':
        return <Building2 className="h-4 w-4" />;
      case 'moderator':
        return <Users className="h-4 w-4" />;
      case 'alumni':
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getInitials = (firstname, lastname) => {
    const first = firstname ? firstname[0] : '';
    const last = lastname ? lastname[0] : '';
    return (first + last).toUpperCase() || '?';
  };

  if (authLoading) {
    return (
      <div className="flex min-h-[70vh] w-full items-center justify-center md:h-[90vh]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-[70vh] w-full items-center justify-center bg-gray-50 md:h-[90vh]">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-4 inline-block rounded-full bg-gray-100 p-6"
          >
            <X className="h-12 w-12 text-gray-400" />
          </motion.div>
          <h3 className="mb-2 text-lg font-medium text-black">Authentication Required</h3>
          <p className="text-sm text-gray-500">Please log in to access the mailbox</p>
        </div>
      </div>
    );
  }

  if (!currentUserData) {
    return (
      <div className="flex min-h-[70vh] w-full items-center justify-center md:h-[90vh]">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-500">Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex min-h-[70vh] w-full flex-col overflow-hidden rounded-xl shadow-2xl md:h-[90vh] md:flex-row ${
        isDarkMode ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      {/* Sidebar */}
      <div
        className={`flex w-full flex-col md:w-80 ${
          isDarkMode
            ? 'border-r border-gray-700 bg-gray-800'
            : 'border-r border-gray-200 bg-gray-50'
        }`}
      >
        {/* Header */}
        <div
          className={`border-b p-4 ${
            isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
          }`}
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Messages
            </h2>
          </div>

          <div className="relative mb-3">
            <Search
              className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
            <input
              type="text"
              placeholder="Search conversations..."
              value={chatSearchTerm}
              onChange={e => setChatSearchTerm(e.target.value)}
              className={`w-full rounded-xl py-3 pl-10 pr-4 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode
                  ? 'border border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                  : 'border border-gray-200 bg-gray-100 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>

          <motion.button
            onClick={() => setShowUserSearch(true)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-700"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <MessageCircle className="h-4 w-4" />
            New Chat
          </motion.button>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {chatsLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-4 rounded-full p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
              >
                <MessageCircle
                  className={`h-12 w-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}
                />
              </motion.div>
              <h3
                className={`mb-2 text-lg font-medium ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {chatSearchTerm ? 'No matching conversations' : 'No messages yet'}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {chatSearchTerm
                  ? 'Try adjusting your search terms'
                  : 'Start a new conversation by clicking the "New Chat" button above'}
              </p>
              {!chatSearchTerm && (
                <motion.button
                  onClick={() => setShowUserSearch(true)}
                  className="mt-4 flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-700"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MessageCircle className="h-4 w-4" />
                  Start New Chat
                </motion.button>
              )}
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              {filteredChats.map(chat => (
                <ChatItem
                  key={chat.id}
                  chat={chat}
                  isSelected={selectedChat?.id === chat.id}
                  currentUserData={currentUserData}
                  onSelect={setSelectedChat}
                  onDelete={handleDeleteChat}
                  onMute={handleMuteChat}
                  onArchive={handleArchiveChat}
                  isDarkMode={isDarkMode}
                />
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={`flex flex-1 flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div
              className={`flex items-center justify-between border-b px-6 py-4 ${
                isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 font-semibold text-white shadow-lg">
                    {selectedChat.otherParticipant
                      ? getInitials(
                          selectedChat.otherParticipant.firstname,
                          selectedChat.otherParticipant.lastname
                        )
                      : '?'}
                  </div>
                  <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white bg-green-500"></div>
                </div>
                <div>
                  <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedChat.otherParticipant
                      ? `${selectedChat.otherParticipant.firstname || ''} ${selectedChat.otherParticipant.lastname || ''}`.trim() ||
                        'Unknown User'
                      : 'Unknown User'}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Online</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className={`rounded-full p-3 transition-colors ${
                    isDarkMode
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <Phone size={20} />
                </button>
                <button
                  className={`rounded-full p-3 transition-colors ${
                    isDarkMode
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <Video size={20} />
                </button>
                <button
                  className={`rounded-full p-3 transition-colors ${
                    isDarkMode
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <MoreVertical size={20} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <MessageList
              chatId={selectedChat.id}
              currentUserId={currentUserData.id}
              isDarkMode={isDarkMode}
              onEditMessage={handleEditMessage}
              onDeleteMessage={handleDeleteMessage}
              editingMessageId={editingMessageId}
              setEditingMessageId={setEditingMessageId}
              editText={editText}
              setEditText={setEditText}
              openMessageOptions={openMessageOptions}
              setOpenMessageOptions={setOpenMessageOptions}
              canEditDelete={true}
            />

            {/* Message Input */}
            <div
              className={`border-t px-6 py-4 ${
                isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
              }`}
            >
              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 ${
                        isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                      }`}
                    >
                      {file.type?.startsWith('image/') ? (
                        <Image
                          size={16}
                          className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}
                        />
                      ) : file.type?.startsWith('video/') ? (
                        <Video
                          size={16}
                          className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}
                        />
                      ) : (
                        <File
                          size={16}
                          className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}
                        />
                      )}
                      <span
                        className={`max-w-[150px] truncate text-xs ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}
                      >
                        {file.name}
                      </span>
                      <button
                        onClick={() => removeFile(index)}
                        className={`rounded p-0.5 transition-colors ${
                          isDarkMode
                            ? 'text-gray-400 hover:bg-gray-600 hover:text-gray-200'
                            : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                        }`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Uploading Files Indicator */}
              {uploadingFiles.length > 0 && (
                <div className="mb-3 flex items-center gap-2 text-sm text-blue-600">
                  <Loader2 size={16} className="animate-spin" />
                  <span>Uploading {uploadingFiles.length} file(s)...</span>
                </div>
              )}

              <div className="flex items-end gap-3">
                <input
                  type="file"
                  id="file-upload-mailbox"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="image/*,video/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                />
                <label
                  htmlFor="file-upload-mailbox"
                  className={`cursor-pointer rounded-full p-2 transition-colors ${
                    isDarkMode
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <Paperclip size={20} />
                </label>
                <div className="relative flex-1">
                  <textarea
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage(e);
                      }
                    }}
                    placeholder="Type your message..."
                    rows={1}
                    className={`w-full resize-none rounded-2xl px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? 'border border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                        : 'border border-gray-200 bg-gray-100 text-gray-900 placeholder-gray-500'
                    }`}
                    style={{ minHeight: '48px', maxHeight: '120px' }}
                  />
                </div>
                <motion.button
                  onClick={sendMessage}
                  disabled={
                    (!newMessage.trim() && selectedFiles.length === 0) || uploadingFiles.length > 0
                  }
                  className="flex items-center gap-2 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {uploadingFiles.length > 0 ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </>
        ) : (
          <div
            className={`flex h-full items-center justify-center ${
              isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
            }`}
          >
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-6 rounded-full p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
              >
                <MessageCircle
                  className={`h-20 w-20 ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}
                />
              </motion.div>
              <h3
                className={`mb-2 text-xl font-semibold ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                Select a conversation to start messaging
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Choose from your existing conversations or start a new one
              </p>
            </div>
          </div>
        )}
      </div>

      {/* User Search Modal */}
      <AnimatePresence>
        {showUserSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={`w-[92%] max-w-md rounded-2xl shadow-2xl sm:w-full ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <div
                className={`flex items-center justify-between border-b px-6 py-4 ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  New Conversation
                </h2>
                <button
                  onClick={() => {
                    setShowUserSearch(false);
                    setSearchTerm('');
                    setSearchResults([]);
                  }}
                  className={`rounded-full p-2 transition-colors ${
                    isDarkMode
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6">
                <div className="relative mb-6">
                  <Search
                    className={`absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transform ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className={`w-full rounded-xl py-3 pl-12 pr-4 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode
                        ? 'border border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                        : 'border border-gray-200 bg-gray-100 text-gray-900 placeholder-gray-500'
                    }`}
                    autoFocus
                  />
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {usersLoading ? (
                    <div className="py-8 text-center">
                      <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Loading users...
                      </p>
                    </div>
                  ) : !searchTerm ? (
                    <div className="py-8 text-center">
                      <Users
                        className={`mx-auto mb-4 h-16 w-16 ${
                          isDarkMode ? 'text-gray-600' : 'text-gray-300'
                        }`}
                      />
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Start typing to search users
                      </p>
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className="py-8 text-center">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No users found matching "{searchTerm}"
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {searchResults.map(user => (
                        <motion.div
                          key={user.id}
                          onClick={() => startNewChat(user)}
                          className={`cursor-pointer rounded-xl p-4 transition-all hover:shadow-md ${
                            isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                          }`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 font-semibold text-white shadow-lg">
                              {getInitials(user.firstname, user.lastname)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex items-center gap-2">
                                <span
                                  className={`font-semibold ${
                                    isDarkMode ? 'text-white' : 'text-gray-900'
                                  }`}
                                >
                                  {`${user.firstname || ''} ${user.lastname || ''}`.trim() ||
                                    'Unknown User'}
                                </span>
                                <div className="flex items-center gap-1">
                                  {getUserIcon(user.role)}
                                  <span
                                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                                      isDarkMode
                                        ? 'bg-gray-700 text-gray-300'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {user.role}
                                  </span>
                                </div>
                              </div>
                              <div
                                className={`truncate text-sm ${
                                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                }`}
                              >
                                {user.email}
                              </div>
                              {(user.program || user.department) && (
                                <div
                                  className={`text-xs ${
                                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                  }`}
                                >
                                  {user.program && user.department
                                    ? `${user.program} • ${user.department}`
                                    : user.program || user.department}
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Popup */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowDeleteConfirm(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={`fixed left-1/2 top-1/2 z-50 w-[90%] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl p-6 shadow-2xl ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <div className="mb-4">
                <h3
                  className={`mb-2 text-lg font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Confirm Delete
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Are you sure you want to delete this{' '}
                  {showDeleteConfirm.type === 'message' ? 'message' : 'conversation'}? This action
                  cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (showDeleteConfirm.type === 'message') {
                      handleDeleteMessage(showDeleteConfirm.messageId, true);
                    } else {
                      handleDeleteChat(showDeleteConfirm.chatId, true);
                    }
                  }}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Mailbox;
